/**
 * ProfitCard — small dashboard widget shown in e-commerce mode.
 * Calls /api/dashboard/ecom-profit and renders revenue / spend / true profit
 * with a colored net-margin badge. Uses the user's ecommerce_costs catalog.
 */
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface ProfitData {
  revenue: number;
  adSpend: number;
  cogs: number;
  shipping: number;
  platformFees: number;
  monthlyOverhead: number;
  oneTime: number;
  totalCosts: number;
  grossProfit: number;
  netProfit: number;
  netMarginPct: number;
  roas: number;
  poas: number;
  currency: string;
}

function fmt(v: number, currency = "PLN"): string {
  try {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v.toFixed(0)} ${currency}`;
  }
}

export function ProfitCard({
  datePreset,
  adAccountId,
}: {
  datePreset: string;
  adAccountId?: string;
}) {
  const { data, isLoading } = useQuery<ProfitData>({
    queryKey: ["/api/dashboard/ecom-profit", datePreset, adAccountId],
    queryFn: () => {
      const p = new URLSearchParams({ datePreset });
      if (adAccountId) p.set("adAccountId", adAccountId);
      return apiRequest("GET", `/api/dashboard/ecom-profit?${p.toString()}`);
    },
    enabled: !!datePreset,
    staleTime: 60_000,
  });

  if (isLoading) {
    // VP-208: placeholder w wymiarach załadowanej karty (nagłówek + kwota + siatka 2×3),
    // żeby karta nie urosła skokowo po policzeniu profitu.
    return (
      <div className="border rounded-xl p-4 bg-white/[0.02]">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-5 w-20 rounded" />
        </div>
        <Skeleton className="h-9 w-44 mb-3" />
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const positive = data.netProfit >= 0;
  const badgeCls = positive
    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
    : "text-red-400 bg-red-500/10 border-red-500/30";

  return (
    <div className="border rounded-xl p-4 bg-white/[0.02]">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground">Zysk netto (po kosztach)</div>
        <span className={`text-xs px-2 py-0.5 rounded border ${badgeCls}`}>
          marża {data.netMarginPct.toFixed(1)}%
        </span>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <div className={`text-3xl font-bold ${positive ? "text-emerald-400" : "text-red-400"}`}>
          {fmt(data.netProfit, data.currency)}
        </div>
        {positive ? (
          <TrendingUp className="w-5 h-5 text-emerald-400" />
        ) : (
          <TrendingDown className="w-5 h-5 text-red-400" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Przychód</span>
          <span className="text-white/80">{fmt(data.revenue, data.currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Wydatek na reklamy</span>
          <span className="text-white/80">{fmt(data.adSpend, data.currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">COGS</span>
          <span className="text-white/80">{fmt(data.cogs, data.currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Wysyłka</span>
          <span className="text-white/80">{fmt(data.shipping, data.currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Prowizje</span>
          <span className="text-white/80">{fmt(data.platformFees, data.currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Stałe (proporcj.)</span>
          <span className="text-white/80">
            {fmt(data.monthlyOverhead + data.oneTime, data.currency)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">ROAS</span>
          <span className="text-white/80">{data.roas.toFixed(2)}x</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">POAS</span>
          <span className={positive ? "text-emerald-400" : "text-red-400"}>
            {data.poas.toFixed(2)}x
          </span>
        </div>
      </div>
    </div>
  );
}
